import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store/store.inteface';
import { FilterDrawer } from 'components/FilterDrawer';
import { getCertificatesAction } from 'store/modules/certificates/actions';
import { Loading } from 'pages/Franchisee/TimelineActions/DocumentRequest/Loading';
import { Wrapper, Title } from './styles';
import { DownloadMultipleButton } from './downloadButton';
import { ShowMoreButton } from './ShowMoreButton';
import { NoContent } from './NoContent';
import { TableCertificates } from './components/Table';

export interface CertificateWrapperProps {
  title: string;
  selectedPeriodRecent: string;
  periodSelect: string;
  setSelectedPeriodRecent: (period: string) => void;
}

export const CertificateWrapper = ({
  title,
  selectedPeriodRecent,
  setSelectedPeriodRecent,
  periodSelect,
}: CertificateWrapperProps) => {
  const { stores } = useSelector(
    (state: ApplicationState) => state.storesContext,
  );
  const { certificates, error } = useSelector(
    (state: ApplicationState) => state.certificates,
  );
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [showMore, setShowMore] = useState(false);
  const dispatch = useDispatch();

  const franchiseCode = stores.map(({ code }) => code).slice(0, 100);

  function handleSelect(certificate, code) {
    setSelected(prevSelected => {
      const newSelection = { certificate, code };
      const isSelected = prevSelected.some(
        selection =>
          selection.certificate === certificate && selection.code === code,
      );

      if (isSelected) {
        return prevSelected.filter(
          selection =>
            selection.certificate !== certificate || selection.code !== code,
        );
      }
      return [...prevSelected, newSelection];
    });
  }

  const certificatesSelected = selected.map(selection => selection.certificate);
  const franchises = selected.map(selection => selection.code);
  const franchiseSelected = [...new Set(franchises)];

  async function handleClearFilters() {
    getCertificates();
  }

  const handleApplyFilters = async filters => {
    const codeString = JSON.stringify(franchiseCode);
    fetchCertificates(
      {
        period: filters.period || selectedPeriodRecent,
      },
      filters.selectedFranchises.length > 0
        ? JSON.stringify(filters.selectedFranchises)
        : codeString,
    );
    if (filters.period) {
      setSelectedPeriodRecent(filters.period);
    }
  };

  const fetchCertificates = useCallback(
    (filters, franchise) => {
      dispatch(
        getCertificatesAction(
          // eslint-disable-next-line object-shorthand
          { period: filters.period, limit: 8, showMore: showMore },
          franchise,
        ),
      );
    },
    [dispatch, showMore],
  );

  const getCertificates = useCallback(() => {
    if (stores.length > 0) {
      const filters = {
        period: selectedPeriodRecent || periodSelect,
      };
      const codeString = JSON.stringify(franchiseCode);

      fetchCertificates(filters, codeString);
    }
  }, [stores, selectedPeriodRecent, periodSelect, fetchCertificates]);

  useEffect(() => {
    getCertificates();
  }, [getCertificates]);

  useEffect(() => {
    setLoading(false);
    if (
      (certificates.response.length === 0 && error === null) ||
      stores.length === 0
    ) {
      setLoading(true);
    }
  }, [certificates, error, stores]);

  return (
    <Wrapper>
      <header>
        <Title>{title}</Title>
        <div
          className="header-content"
          style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
        >
          <DownloadMultipleButton
            selected={certificatesSelected}
            franshiceCode={franchiseSelected}
          />
          <FilterDrawer
            franchises={stores}
            onApplyFilter={handleApplyFilters}
            onClearFilter={handleClearFilters}
          />
        </div>
      </header>
      {!error && (
        <>
          <TableCertificates
            certificates={certificates}
            selected={selected}
            handleSelect={(id, pdvNumber) => handleSelect(id, pdvNumber)}
          />
          <footer>
            <ShowMoreButton
              setShowMore={setShowMore}
              hasMore={certificates.hasMore}
              size={certificates.response.length}
            />
          </footer>
        </>
      )}
      {loading && <Loading />}
      {error && <NoContent error={error} />}
    </Wrapper>
  );
};
